import { Link } from "@tanstack/react-router";
import { CircleDot } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { isMissingEndpoint, useRecentActivity, type AuditEvent } from "@/lib/api-hooks";
import { cn } from "@/lib/utils";

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const OUTCOME_TONE: Record<AuditEvent["outcome"], string> = {
  success: "text-success",
  failure: "text-destructive",
  denied: "text-warning",
  pending: "text-muted-foreground",
};

export function RecentActivityPanel(): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, error } = useRecentActivity(8);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-semibold">
          {t("dashboard.activity.title")}
        </CardTitle>
        <Link to="/audit" className="text-xs font-medium text-primary hover:underline">
          {t("dashboard.activity.see_all")} →
        </Link>
      </CardHeader>
      <CardContent className="pb-3">
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        )}

        {error && !isLoading && (
          <div className="py-1 text-xs text-muted-foreground">
            {isMissingEndpoint(error)
              ? t("dashboard.activity.coming_soon")
              : t("dashboard.activity.unavailable")}
          </div>
        )}

        {!isLoading && !error && data && data.length === 0 && (
          <div className="py-1 text-xs text-muted-foreground">
            {t("dashboard.activity.empty")}
          </div>
        )}

        {data && data.length > 0 && (
          <ul className="divide-y divide-border text-sm">
            {data.map((evt) => (
              <li key={evt.id} className="flex items-baseline gap-2 py-1.5">
                <span className="w-12 font-mono text-xs text-muted-foreground tabular-nums">
                  {formatTime(evt.occurred_at)}
                </span>
                <CircleDot className={cn("size-3 shrink-0", OUTCOME_TONE[evt.outcome])} />
                <code className="font-mono text-xs">{evt.event_type}</code>
                {evt.summary && (
                  <span className="truncate text-muted-foreground">
                    · {evt.summary}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
